/**
 * Data protection — replaces the /data-protection ComingSoonPage stub
 * (contest brief, 2026-07-11).
 *
 * Plain-language account of what data exists, where it lives, and what a
 * parent can always do — drawn from the canonical compliance matrix
 * (blueprint Annex F.1). Rules honored: "designed for", never "compliant
 * with"; no certifications claimed; the operator's position stated the way
 * the blueprint states it.
 */
import { Link } from "react-router-dom";
import { Smartphone, Server, Download, Trash2, Baby, Mail } from "lucide-react";
import { useSEO } from "@/lib/seo";

const ON_DEVICE = [
  "Your child's boards and every tile on them",
  "Family photos placed on tiles",
  "Voice recordings — your voice, your child's name for Grandma",
  "Tap history and progress data",
  "Names, nicknames, and anything you type about your child",
];

const ON_SERVERS = [
  {
    what: "Your adult account",
    detail: "Email and a hashed password. Deleted when you delete the account.",
  },
  {
    what: "Consent records",
    detail: "A dated record of what you agreed to — that record is the point.",
  },
  {
    what: "Credit numbers",
    detail: "How many credits your account holds. Numbers, no content.",
  },
  {
    what: "Encrypted professional mail",
    detail:
      "When you invite a therapist, suggestions travel as encrypted envelopes " +
      "we cannot open. Undelivered envelopes are dropped after 72 hours; " +
      "delivered ones are deleted on receipt.",
  },
];

export default function DataProtection() {
  useSEO({
    title: "Data protection",
    description:
      "What data SonSpeak holds, where it lives, and what parents can " +
      "always do: export everything, delete everything, anytime.",
    path: "/data-protection",
  });

  return (
    <div className="min-h-screen bg-[color:var(--cream)]" data-testid="data-protection-page">
      <section className="max-w-3xl mx-auto px-5 sm:px-8 pt-12 sm:pt-16 pb-8">
        <p className="display text-xs font-bold uppercase tracking-[0.24em] text-[color:var(--coral-deep)] mb-3">
          Data protection
        </p>
        <h1 className="display text-3xl sm:text-4xl font-extrabold leading-tight mb-4">
          Your family&rsquo;s data, in plain language.
        </h1>
        <p className="text-base sm:text-lg text-[color:var(--ink)]/80 leading-relaxed">
          Two lists tell the whole story: what stays on your device, and the
          little we hold so your account works. There is no third list.
        </p>
      </section>

      <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-8" aria-labelledby="dp-device-heading">
        <div className="brutal-card bg-white p-6">
          <h2 id="dp-device-heading" className="display text-xl font-extrabold mb-4 flex items-center gap-2">
            <Smartphone size={20} aria-hidden /> Lives only on your device
          </h2>
          <ul className="space-y-2">
            {ON_DEVICE.map((item) => (
              <li key={item} className="flex items-start gap-2 text-sm leading-relaxed">
                <span className="mt-1 w-2 h-2 rounded-full bg-[color:var(--coral)] shrink-0" aria-hidden />
                {item}
              </li>
            ))}
          </ul>
          <p className="text-sm text-[color:var(--ink-soft)] mt-4 leading-relaxed">
            These are created and stored on the family&rsquo;s device. We do not
            collect them, we cannot read them, and no consent setting changes
            that. Custom text about your child never leaves the device.
          </p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-8" aria-labelledby="dp-server-heading">
        <div className="brutal-card bg-white p-6">
          <h2 id="dp-server-heading" className="display text-xl font-extrabold mb-4 flex items-center gap-2">
            <Server size={20} aria-hidden /> What our servers hold
          </h2>
          <div className="space-y-4">
            {ON_SERVERS.map((row) => (
              <div key={row.what}>
                <div className="display font-bold text-sm">{row.what}</div>
                <p className="text-sm text-[color:var(--ink-soft)] leading-relaxed">{row.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-8" aria-labelledby="dp-rights-heading">
        <h2 id="dp-rights-heading" className="display text-xl font-extrabold mb-4">
          What you can always do
        </h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="brutal-card bg-[color:var(--paper)] p-5">
            <div className="display font-bold text-sm mb-1 flex items-center gap-2">
              <Download size={16} aria-hidden /> Export everything
            </div>
            <p className="text-sm text-[color:var(--ink-soft)] leading-relaxed">
              Boards, photos, recordings, history — yours to take, anytime,
              even if your plan has lapsed. Payment gates the service, never
              your family&rsquo;s data.
            </p>
          </div>
          <div className="brutal-card bg-[color:var(--paper)] p-5">
            <div className="display font-bold text-sm mb-1 flex items-center gap-2">
              <Trash2 size={16} aria-hidden /> Delete everything
            </div>
            <p className="text-sm text-[color:var(--ink-soft)] leading-relaxed">
              Deleting a profile destroys its keys — the data becomes
              unreadable, permanently. We always offer archiving first so a
              tired tap can&rsquo;t erase a child&rsquo;s history.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-8" aria-labelledby="dp-children-heading">
        <div className="brutal-card bg-white p-6">
          <h2 id="dp-children-heading" className="display text-xl font-extrabold mb-3 flex items-center gap-2">
            <Baby size={20} aria-hidden /> Children&rsquo;s privacy
          </h2>
          <p className="text-sm leading-relaxed mb-3">
            SonSpeak is designed around children&rsquo;s-privacy law from the
            ground up: the operator does not collect children&rsquo;s personal
            information. The child&rsquo;s content — photos and voice recordings
            included — is created and stored on the family&rsquo;s device under
            keys we do not possess.
          </p>
          <p className="text-sm text-[color:var(--ink-soft)] leading-relaxed">
            Parental-consent records exist so that <em>you</em> hold the
            record of what you agreed to. Voice recordings are treated as the
            most sensitive class of data there is: they never leave the
            device, and no AI ever generates or receives your child&rsquo;s voice.
          </p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-16">
        <div className="brutal-card bg-[color:var(--gold)] p-6 text-center">
          <h2 className="display text-lg font-extrabold mb-2 flex items-center justify-center gap-2">
            <Mail size={18} aria-hidden /> Questions about your data?
          </h2>
          <p className="text-sm leading-relaxed mb-4">
            Write to us and a human answers. See also{" "}
            <Link className="underline font-semibold" to="/security">Security</Link>{" "}
            and{" "}
            <Link className="underline font-semibold" to="/privacy-promise">our privacy promise</Link>.
          </p>
          <a className="pill-btn primary" href="mailto:hello@sonspeak.com" data-testid="dp-contact">
            hello@sonspeak.com
          </a>
        </div>
      </section>
    </div>
  );
}
